/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';
import compact from 'lodash/compact';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';

import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { ServiceLocationId, ServiceLocationIdValue } from '@dreryk/egabinet-healthcare-provider-published-language-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import {
   DayOfWeek,
   TherapeuticGroupIssue,
   TherapeuticGroupOccupancy,
   TherapeuticGroupSimple,
   TherapeuticGroupSimpleQuery,
   TherapeuticGroupStatus,
   TherapeuticGroupType,
   TimeOfDay,
} from '@dreryk/egabinet-group-session-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import {
   createDynamicColumnsWidth,
   FilterPanel,
   TableColumnConfiguration,
   TextPlaceholder,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';
import { toHealthcareProviderIdentifier } from '~libs.shared.healthcare-provider-location';

import { translationKeys } from '../translations';
import { NO_SERVICE_ITEM } from '../filters/controllers/servicesController/ServicesController.constants';
import { GroupSessionsFilterPanelSearchParam } from '../filters/GroupSessionsFilterPanel.types';
import { statusDeserializer } from '../filters/controllers/status/StatusController.utils';
import { getTimeOfDayByInternalId } from '../filters/controllers/schedule/ScheduleController.utils';
import { groupTypeDeserializer } from '../filters/controllers/groupType/GroupTypeController.utils';
import { occupancyDeserializer } from '../filters/controllers/occupancy/OccupancyController.utils';
import { issueDeserializer } from '../filters/controllers/issue/IssueController.utils';

import { StatusCell } from './cells/StatusCell';
import { TypeCell } from './cells/TypeCell';
import { PractitionersCell } from './cells/PractitionersCell';
import { ReceptionServiceCell } from './cells/ReceptionServicesCell';
import { NoteCell } from './cells/NoteCell';
import { ScheduleDaysCell } from './cells/ScheduleCell';
import { ParticipantAgeCell } from './cells/ParticipantAgeCell';
import { ParticipantsLimitCell } from './cells/ParticipantsLimitCell';
import { ServiceProvidingLocationCell } from './cells/ServiceProvidingLocationCell';

export const useGroupSessionsTableColumnConfiguration = (): TableColumnConfiguration<TherapeuticGroupSimple>[] => {
   const columnsWidth = useMemo(() => {
      return createDynamicColumnsWidth({
         name: protectFeature('searchingPrivateMedicalServices', '33%', '50%'),
         note: '32px',
         type: '106px',
         practitioners: protectFeature('searchingPrivateMedicalServices', '33%', '50%'),
         receptionService: protectFeature('searchingPrivateMedicalServices', '33%', '0px'),
         scheduleDays: '136px',
         participantsAgeRange: '120px',
         placesLimit: '100px',
         serviceProvidingLocation: '140px',
         status: '164px',
      });
   }, []);

   return useMemo(
      () =>
         compact([
            {
               name: 'name',
               accessor: 'name',
               label: translationKeys.name,
               width: columnsWidth.name,
               sortingDisabled: true,
               renderer: ({ bean }) => {
                  return <span>{bean.name}</span>;
               },
            },
            {
               name: 'note',
               accessor: 'note',
               width: columnsWidth.note,
               sortingDisabled: true,
               renderer: ({ bean }) => {
                  if (!bean.note) {
                     return null;
                  }

                  return <NoteCell note={bean.note} />;
               },
            },
            {
               name: 'type',
               accessor: 'type',
               label: translationKeys.type,
               width: columnsWidth.type,
               sortingDisabled: true,
               renderer: ({ bean }) => {
                  return <TypeCell type={bean.type} />;
               },
            },
            {
               name: 'practitioners',
               accessor: 'practitioners',
               label: translationKeys.practitioners,
               width: columnsWidth.practitioners,
               sortingDisabled: true,
               placeholder: TextPlaceholder.NO_DATA,
               renderer: ({ bean }) => {
                  if (isEmpty(bean.practitioners)) {
                     return null;
                  }

                  return <PractitionersCell practitioners={bean.practitioners} />;
               },
            },
            protectFeature('searchingPrivateMedicalServices', {
               name: 'receptionService',
               accessor: 'receptionService',
               label: translationKeys.service,
               width: columnsWidth.receptionService,
               sortingDisabled: true,
               placeholder: TextPlaceholder.NO_DATA,
               renderer: ({ bean }) => {
                  if (!bean.receptionService) {
                     return null;
                  }

                  return <ReceptionServiceCell receptionService={bean.receptionService} />;
               },
            }),
            {
               name: 'scheduleDays',
               accessor: 'scheduleDays',
               label: translationKeys.scheduleDays,
               width: columnsWidth.scheduleDays,
               sortingDisabled: true,
               placeholder: TextPlaceholder.NO_DATA,
               renderer: ({ bean }) => {
                  if (isEmpty(bean.scheduleDays)) {
                     return null;
                  }

                  return <ScheduleDaysCell scheduleDays={bean.scheduleDays} />;
               },
            },
            {
               name: 'participantsAgeRange',
               accessor: noop,
               label: translationKeys.participantAge,
               width: columnsWidth.participantsAgeRange,
               sortingDisabled: true,
               placeholder: TextPlaceholder.NO_DATA,
               renderer: ({ bean }) => {
                  if (!bean.minAge || !bean.maxAge) {
                     return null;
                  }

                  return <ParticipantAgeCell minAge={bean.minAge} maxAge={bean.maxAge} />;
               },
            },
            {
               name: 'placesLimit',
               accessor: noop,
               label: translationKeys.placesLimit,
               width: columnsWidth.placesLimit,
               sortingDisabled: true,
               renderer: ({ bean }) => {
                  return (
                     <ParticipantsLimitCell
                        minParticipants={bean.minParticipants}
                        maxParticipants={bean.maxParticipants}
                        participantsCount={bean.participantsCount}
                     />
                  );
               },
            },
            {
               name: 'serviceProvidingLocation',
               accessor: noop,
               label: translationKeys.serviceProvidingLocationAbbreviation,
               width: columnsWidth.serviceProvidingLocation,
               sortingDisabled: true,
               placeholder: TextPlaceholder.NO_DATA,
               renderer: ({ bean }) => {
                  if (isEmpty(bean.scheduleDays)) {
                     return null;
                  }

                  return <ServiceProvidingLocationCell scheduleDays={bean.scheduleDays} />;
               },
            },
            {
               name: 'status',
               accessor: 'status',
               label: translationKeys.status,
               width: columnsWidth.status,
               sortingDisabled: true,
               renderer: ({ bean }) => {
                  return <StatusCell status={bean.status} />;
               },
            },
         ]),
      [columnsWidth]
   );
};

type GroupSessionsPanelDeserializedParamsMap = {
   [GroupSessionsFilterPanelSearchParam.PRACTITIONER]: 'practitioners';
   [GroupSessionsFilterPanelSearchParam.HEALTHCARE_PROVIDER_LOCATION]: 'healthcareProviderLocations';
   [GroupSessionsFilterPanelSearchParam.STATUS]: 'statuses';
   [GroupSessionsFilterPanelSearchParam.SERVICES]: 'services';
   [GroupSessionsFilterPanelSearchParam.SCHEDULE_DAY_OF_WEEK]: 'scheduleDayOfWeek';
   [GroupSessionsFilterPanelSearchParam.SCHEDULE_TIMES_OF_DAY]: 'scheduleTimesOfDay';
   [GroupSessionsFilterPanelSearchParam.GROUP_TYPE]: 'groupTypes';
   [GroupSessionsFilterPanelSearchParam.OCCUPANCY]: 'occupancy';
   [GroupSessionsFilterPanelSearchParam.ISSUE]: 'issue';
};

type GroupSessionsPanelDeserializedFilters = {
   practitioners: PractitionerId[];
   healthcareProviderLocations: ServiceLocationId[];
   statuses: TherapeuticGroupStatus[];
   services: (ReceptionServiceId | null)[];
   scheduleDayOfWeek: DayOfWeek | null;
   scheduleTimesOfDay: TimeOfDay[];
   groupTypes: TherapeuticGroupType[];
   occupancy: TherapeuticGroupOccupancy | null;
   issue: TherapeuticGroupIssue | null;
};

/* eslint-disable max-lines */
// eslint-disable-next-line max-lines-per-function
export const useGroupSessionsTableLazyLoadedDataConfigurationByCqrsQuery = (): LazyLoadedDataConfigurationByCqrsQuery<TherapeuticGroupSimple> => {
   const deserializedFilters = FilterPanel.useDeserializedFilters<
      GroupSessionsPanelDeserializedFilters,
      GroupSessionsPanelDeserializedParamsMap
   >({
      [GroupSessionsFilterPanelSearchParam.PRACTITIONER]: (currentParamValue) => ({
         practitioners: FilterPanel.paramToNumberArray(currentParamValue).map(PractitionerId.of),
      }),
      [GroupSessionsFilterPanelSearchParam.HEALTHCARE_PROVIDER_LOCATION]: (currentParamValue) => ({
         healthcareProviderLocations: FilterPanel.paramToArray<ServiceLocationId, 'string'>(
            currentParamValue,
            'string',
            (value) => {
               const healthcareProviderIdentifier = toHealthcareProviderIdentifier(
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                  value as ServiceLocationIdValue
               );
               return ServiceLocationId.of(healthcareProviderIdentifier.type, healthcareProviderIdentifier.id);
            }
         ),
      }),
      [GroupSessionsFilterPanelSearchParam.STATUS]: (currentParamValue) => ({
         statuses: currentParamValue ? statusDeserializer(currentParamValue) : [],
      }),
      [GroupSessionsFilterPanelSearchParam.SERVICES]: (currentParamValue) => ({
         services: FilterPanel.paramToArray(currentParamValue, 'string', (mappedParamValue) => {
            if (mappedParamValue === NO_SERVICE_ITEM) {
               return null;
            }
            return ReceptionServiceId.of(Number(mappedParamValue));
         }),
      }),
      [GroupSessionsFilterPanelSearchParam.SCHEDULE_DAY_OF_WEEK]: (currentParamValue) => ({
         scheduleDayOfWeek: FilterPanel.paramToSingleValue(
            currentParamValue,
            'string',
            (dayOfWeek) => (dayOfWeek || null) as DayOfWeek | null
         ),
      }),
      [GroupSessionsFilterPanelSearchParam.SCHEDULE_TIMES_OF_DAY]: (currentParamValue) => ({
         scheduleTimesOfDay: FilterPanel.paramToNumberArray(currentParamValue)
            .map(getTimeOfDayByInternalId)
            .filter(Boolean) as TimeOfDay[],
      }),
      [GroupSessionsFilterPanelSearchParam.GROUP_TYPE]: (currentParamValue) => ({
         groupTypes: currentParamValue ? groupTypeDeserializer(currentParamValue) : [],
      }),
      [GroupSessionsFilterPanelSearchParam.OCCUPANCY]: (currentParamValue) => ({
         occupancy: occupancyDeserializer(currentParamValue),
      }),
      [GroupSessionsFilterPanelSearchParam.ISSUE]: (currentParamValue) => ({
         issue: issueDeserializer(currentParamValue),
      }),
   });

   return useLazyLoadedDataConfigurationByCqrsQuery(() => {
      const query = new TherapeuticGroupSimpleQuery();
      query.practitionerIds = Slot.emptyIfNull(
         deserializedFilters.practitioners.length > 0 ? deserializedFilters.practitioners : null
      );
      query.serviceLocationIds = Slot.emptyIfNull(
         deserializedFilters.healthcareProviderLocations.length > 0
            ? deserializedFilters.healthcareProviderLocations
            : null
      );
      query.statuses = Slot.emptyIfNull(deserializedFilters.statuses.length > 0 ? deserializedFilters.statuses : null);
      query.receptionServiceIds = (Slot.emptyIfNull(
         deserializedFilters.services.length > 0 ? deserializedFilters.services : null
         // TODO: DREGAB-26860 Naprawić typescript-generator https://dreryk.atlassian.net/browse/DREGAB-26860
      ) as unknown) as Slot<ReceptionServiceId[]>;
      query.scheduleDayOfWeek = Slot.emptyIfNull(deserializedFilters.scheduleDayOfWeek);
      query.scheduleTimesOfDay = Slot.emptyIfNull(
         deserializedFilters.scheduleTimesOfDay.length > 0 ? deserializedFilters.scheduleTimesOfDay : null
      );
      query.types = Slot.emptyIfNull(deserializedFilters.groupTypes.length > 0 ? deserializedFilters.groupTypes : null);
      query.occupancy = Slot.emptyIfNull(deserializedFilters.occupancy);
      query.issue = Slot.emptyIfNull(deserializedFilters.issue);
      return query;
   });
};
